import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/apiClient";

// Query Keys
export const bloodTestKeys = {
  all: ["bloodTests"],
  list: (filters) => ["bloodTests", "list", filters],
  detail: (id) => ["bloodTests", "detail", id],
  byPatient: (patientId) => ["bloodTests", "patient", patientId],
  types: () => ["bloodTests", "types"],
};

// Get All Blood Tests Hook
export const useBloodTests = (filters = {}) => {
  return useQuery({
    queryKey: bloodTestKeys.list(filters),
    queryFn: async () => {
      return await apiClient.get("/api/blood-tests", filters);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Get Blood Test by ID Hook
export const useBloodTest = (testId) => {
  return useQuery({
    queryKey: bloodTestKeys.detail(testId),
    queryFn: async () => {
      return await apiClient.get(`/api/blood-tests/${testId}`);
    },
    enabled: !!testId,
    staleTime: 5 * 60 * 1000,
  });
};

// Get Patient Blood Tests Hook
export const usePatientBloodTests = (patientId) => {
  return useQuery({
    queryKey: bloodTestKeys.byPatient(patientId),
    queryFn: async () => {
      return await apiClient.get(`/api/blood-tests/patient/${patientId}`);
    },
    enabled: !!patientId,
    staleTime: 2 * 60 * 1000, // 2 minutes - medical data changes more frequently
  });
};

// Get Blood Test Types Hook
export const useBloodTestTypes = () => {
  return useQuery({
    queryKey: bloodTestKeys.types(),
    queryFn: async () => {
      return await apiClient.get("/api/blood-tests/types");
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
  });
};

// Create Blood Test Mutation
export const useCreateBloodTest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (testData) => {
      return await apiClient.post("/api/blood-tests", testData);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: bloodTestKeys.all });

      if (variables.patientId) {
        queryClient.invalidateQueries({
          queryKey: bloodTestKeys.byPatient(variables.patientId),
        });
      }
    },
  });
};

// Update Blood Test Mutation
export const useUpdateBloodTest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ testId, testData }) => {
      return await apiClient.put(`/api/blood-tests/${testId}`, testData);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: bloodTestKeys.all });
      queryClient.invalidateQueries({
        queryKey: bloodTestKeys.detail(variables.testId),
      });
    },
  });
};

// Delete Blood Test Mutation
export const useDeleteBloodTest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (testId) => {
      return await apiClient.delete(`/api/blood-tests/${testId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bloodTestKeys.all });
    },
  });
};
